import { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import logoImg from "../../assets/logo.webp";

const navLinks = [
  {
    name: "Home",
    path: "/",
  },
  {
    name: "How it works",
    path: "/how-it-works",
  },
  {
    name: "Loan products",
    path: "/loan-products",
  },
  {
    name: "For DSAs",
    path: "/for-dsas",
  },
  {
    name: "For Lenders",
    path: "/for-lenders",
  },
  {
    name: "About",
    path: "/about",
  },
  {
    name: "Contact",
    path: "/contact",
  },
];

function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "border-b border-[#e2e9e5] bg-white/90 backdrop-blur-xl shadow-[0_4px_24px_rgba(0,0,0,0.06)]"
          : "border-b border-[#e2e9e5]/70 bg-white"
      }`}
    >
      <nav
        className="section-container flex h-20 items-center justify-between"
        aria-label="Main navigation"
      >
        {/* Logo */}
        <Link
          to="/"
          onClick={closeMenu}
          className="flex shrink-0 items-center gap-2.5"
          aria-label="Finverse Home"
        >
          <img
            src={logoImg}
            alt="Finverse"
            className="h-8 sm:h-9 w-auto object-contain"
            loading="eager"
          />
          <span className="brand-gradient-text font-jakarta text-[22px] font-bold tracking-[-0.6px]">
            Finverse
          </span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden items-center gap-1.5 lg:flex">
          {navLinks.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              end={link.path === "/"}
              className={({ isActive }) =>
                `group relative px-3.5 py-2 font-jakarta text-[15px] transition-colors duration-200 ${
                  isActive
                    ? "font-bold text-[var(--mint-green)]"
                    : "font-semibold text-black hover:text-[var(--mint-green)]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span>{link.name}</span>
                  {/* Simple Clean Mint-Green Underline Bar (Same Mint-Green Color) */}
                  <span
                    className={`absolute bottom-0 left-3.5 right-3.5 h-[2.5px] rounded-full bg-[var(--mint-green)] transition-all duration-300 ease-out origin-center ${
                      isActive
                        ? "scale-x-100 opacity-100"
                        : "scale-x-0 opacity-0 group-hover:scale-x-75 group-hover:opacity-60"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </div>

        {/* Desktop CTA */}
        <Link
          to="/contact"
          className="hidden shrink-0 rounded-full bg-[var(--mint-green)] px-[22px] py-[11px] font-jakarta text-[15px] font-semibold text-[#071b17] shadow-sm transition-all duration-200 hover:scale-[1.02] hover:brightness-105 outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 active:outline-none select-none lg:block"
        >
          Get started
        </Link>

        {/* Mobile Menu Button */}
        <button
          type="button"
          onClick={() => setIsMenuOpen((prev) => !prev)}
          className="flex h-10 w-10 items-center justify-center rounded-lg text-black transition-colors hover:bg-black/5 outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 lg:hidden"
          aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={isMenuOpen}
          aria-controls="mobile-navigation"
        >
          {isMenuOpen ? (
            <X size={25} strokeWidth={2.2} />
          ) : (
            <Menu size={25} strokeWidth={2.2} />
          )}
        </button>
      </nav>

      {/* Mobile Navigation */}
      <div
        id="mobile-navigation"
        className={`border-t border-[#e2e9e5] bg-white/95 backdrop-blur-2xl lg:hidden ${
          isMenuOpen ? "block" : "hidden"
        }`}
      >
        <div className="section-container py-4">
          <div className="flex flex-col gap-1">
            {navLinks.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                end={link.path === "/"}
                onClick={closeMenu}
                className={({ isActive }) =>
                  `rounded-xl px-4 py-3 font-jakarta text-[16px] transition-all duration-200 ${
                    isActive
                      ? "bg-[var(--mint-green)]/15 font-bold text-[var(--mint-green)]"
                      : "font-semibold text-black hover:bg-[var(--mint-green)]/10 hover:text-[var(--mint-green)]"
                  }`
                }
              >
                {link.name}
              </NavLink>
            ))}

            {/* Mobile CTA */}
            <Link
              to="/contact"
              onClick={closeMenu}
              className="mt-3 rounded-full bg-[var(--mint-green)] px-5 py-3 text-center font-jakarta text-[15px] font-semibold text-[#071b17] shadow-sm transition-all duration-200 hover:brightness-105 outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 active:outline-none select-none"
            >
              Get started
            </Link>
          </div>
        </div>
      </div>

      {/* Smooth Logo Dot Breathing Blink */}
      <style>{`
        @keyframes logoDotBlink {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.2;
            transform: scale(0.75);
          }
        }
        .logo-dot-blink {
          animation: logoDotBlink 2.2s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .logo-dot-blink {
            animation: none;
          }
        }
      `}</style>
    </header>
  );
}

export default Navbar;